import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const goals = [
  {
    title: "Financial Inclusion",
    icon: AccountBalanceIcon,
    description: "Extend financial services to underserved communities, ensuring access to credit and banking solutions for sustainable growth.",
    color: "#1565C0"
  },
  {
    title: "Women Empowerment",
    icon: GroupsIcon,
    description: "Foster economic independence among women through microfinance initiatives and skill development programs.",
    color: "#2E7D32"
  },
  {
    title: "Sustainable Development",
    icon: TrendingUpIcon,
    description: "Promote environmentally conscious businesses and sustainable practices within our community.",
    color: "#C62828"
  }
];

const VisionMission = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box 
      sx={{ 
        pt: { xs: 8, md: 12 },
        pb: { xs: 8, md: 12 },
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,118,210,0.03) 0%, rgba(25,118,210,0) 70%)',
          top: '0%',
          right: '-20%',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156,39,176,0.03) 0%, rgba(156,39,176,0) 70%)',
          bottom: '0%',
          left: '-20%',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Vision Section */}
        <Fade in={true} timeout={1000}>
          <Box sx={{ mb: { xs: 6, md: 8 }, textAlign: 'center' }}>
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
                display: 'block'
              }}
            >
              OUR VISION
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                mb: 3
              }}
            >
              <VisibilityIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                  color: 'text.primary',
                }}
              >
                Empowering Communities Through Finance
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8
              }}
            >
              To create a financially inclusive society where every individual has access to opportunities for economic growth and self-reliance.
            </Typography>
          </Box>
        </Fade>

        {/* Mission Section */}
        <Fade in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
          <Box sx={{ mb: { xs: 8, md: 10 } }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  mb: 1,
                  display: 'block'
                }}
              >
                OUR MISSION
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  mb: 3
                }}
              >
                <TrackChangesIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                    color: 'text.primary',
                  }}
                >
                  Our Purpose & Promise
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 2
                    }}
                  >
                    What We Do
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      fontSize: '1.1rem'
                    }}
                  >
                    We provide accessible financial services and support to underserved communities, focusing on women entrepreneurs and small businesses. Through our microfinance initiatives, we aim to break the cycle of poverty and create sustainable economic opportunities.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 2
                    }}
                  >
                    How We Do It
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      fontSize: '1.1rem'
                    }}
                  >
                    Through innovative financial products, comprehensive training programs, and continuous support, we empower individuals to achieve financial independence. Our approach combines responsible lending with capacity building to ensure sustainable impact.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Goals Section */}
        <Fade in={true} timeout={1000} style={{ transitionDelay: '400ms' }}>
          <Box>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  mb: 1,
                  display: 'block'
                }}
              >
                STRATEGIC GOALS
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  mb: 3
                }}
              >
                <EmojiObjectsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                    color: 'text.primary',
                  }}
                >
                  Our Objectives
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={4}>
              {goals.map((goal, index) => {
                const Icon = goal.icon;
                return (
                  <Grid item xs={12} md={4} key={index}>
                    <Fade in={true} timeout={1000} style={{ transitionDelay: `${(index + 3) * 200}ms` }}>
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                          },
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            bgcolor: goal.color
                          }}
                        />
                        <CardContent sx={{ p: 4 }}>
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: `${goal.color}15`,
                              mb: 2.5
                            }}
                          >
                            <Icon sx={{ fontSize: 32, color: goal.color }} />
                          </Box>
                          <Typography
                            variant="h5"
                            gutterBottom
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '1.25rem', md: '1.4rem' },
                              color: 'text.primary',
                              mb: 1.5
                            }}
                          >
                            {goal.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.95rem', md: '1rem' },
                              lineHeight: 1.7
                            }}
                          >
                            {goal.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default VisionMission;